import { selectDoesUserHaveRbacPermission } from 'core/userContext';
import startCase from 'lodash/startCase';
import { CAMPAIGN_SETTINGS_EDITABLE_STATES } from 'modules/campaigns/lib/constants';
import { useExperimentTakeover } from 'modules/experiments/takeover';
import { FC, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import {
  createCustomDeserializationOptions,
  createSerializationOptionsForEnumValue,
  serializationOptionsForBooleanValue,
  serializationOptionsForNumberValue,
  useSearchParamState,
} from 'utils/hooks/useSearchParamState';
import { PROJECT_PERMISSIONS } from 'utils/permissions';
import { useCampaignStepsState } from '../../hooks/useCampaignStepsState';
import { CampaignEditorView, CampaignStep } from '../../types';
import { EditorContext, EditorContextState } from './EditorContext';

import { useCampaignContext } from '../CampaignContext';

const deserializeFn = (view?: string) => {
  const campaignEditorView = startCase(view?.toLocaleLowerCase()).replace(
    / /g,
    '',
  ) as keyof typeof CampaignEditorView; // if it can not cast the value as a key of Campaign step it will be empty and then we can default.
  const currentView =
    CampaignEditorView[
      campaignEditorView in CampaignEditorView ? campaignEditorView : CampaignEditorView.Edit
    ];
  return currentView;
};

export const EditorProvider: FC<React.PropsWithChildren<unknown>> = ({ children }) => {
  const { openExperimentTakeover } = useExperimentTakeover();
  const { campaign } = useCampaignContext();

  const asf = createCustomDeserializationOptions<CampaignEditorView>({
    stateValueWhenSearchParamIsInvalid: CampaignEditorView.Edit,
    deserializeFn,
  });

  const {
    campaignSteps,
    viewCampaignStep,
    closeCampaignStep,
    completeCampaignStep,
    editCampaignStep,
    currentEditableStep,
  } = useCampaignStepsState();
  const canDraftMessaging = useSelector(selectDoesUserHaveRbacPermission)(
    PROJECT_PERMISSIONS.DraftMessaging,
  );

  const [campaignEditorView, setCampaignEditorView] = useSearchParamState({
    searchParamKey: 'view',
    stateValueWhenSearchParamIsUnset: CampaignEditorView.Edit,
    pushChangesToBrowserHistory: false,
    ...asf,
  });

  const [isExperimentModalOpen, setIsExperimentModalOpen] = useSearchParamState({
    searchParamKey: 'isExperimentModalOpen',
    stateValueWhenSearchParamIsUnset: false,
    ...serializationOptionsForBooleanValue,
  });

  const [campaignEditorStep] = useSearchParamState({
    searchParamKey: 'step',
    stateValueWhenSearchParamIsUnset: null,
    ...createSerializationOptionsForEnumValue({
      enumKeyValueMap: CampaignStep,
      stateValueWhenSearchParamIsInvalid: null,
    }),
  });

  const [experimentId] = useSearchParamState({
    searchParamKey: 'experimentId',
    stateValueWhenSearchParamIsUnset: 0,
    ...serializationOptionsForNumberValue,
  });

  const [isAudienceTakeoverOpen, setIsAudienceTakeoverOpen] = useState(false);
  const [isAlertHistorySlideOverOpen, setIsAlertHistorySlideOverOpen] = useState(false);
  const [isDisplayPriorityTakeoverOpen, setIsDisplayPriorityTakeoverOpen] = useState(false);
  const [isDisplayUntilTakeoverOpen, setIsDisplayUntilTakeoverOpen] = useState(false);

  const handleSetIsAudienceTakeoverOpen = (isOpen: boolean) => {
    if (isOpen) {
      editCampaignStep(CampaignStep.Audience);
    } else {
      viewCampaignStep(CampaignStep.Audience);
    }
    setIsAudienceTakeoverOpen(isOpen);
  };

  const handleSetIsDisplayPriorityTakeoverOpen = (isOpen: boolean) => {
    setIsDisplayPriorityTakeoverOpen(isOpen);
  };

  const handleSetIsDisplayUntilTakeoverOpen = (isOpen: boolean) => {
    setIsDisplayUntilTakeoverOpen(isOpen);
  };

  /**
   * For non-editable campaigns, we don't allow viewing the `Edit` view.
   * This redirect users to the `Summary` view for those states
   */
  useEffect(() => {
    if (
      campaign?.campaignState &&
      !CAMPAIGN_SETTINGS_EDITABLE_STATES.includes(campaign.campaignState)
    ) {
      setCampaignEditorView(CampaignEditorView.Summary);
    }
  }, [campaign]);

  useEffect(() => {
    if (canDraftMessaging && campaignEditorStep) {
      editCampaignStep(campaignEditorStep);
    }
  }, [campaignEditorStep]);

  useEffect(() => {
    openExperimentTakeover(experimentId);
  }, [experimentId]);

  const value: EditorContextState = {
    campaignSteps,
    experimentId,
    canDraftMessaging,
    isExperimentModalOpen,
    setIsExperimentModalOpen,
    completeCampaignStep,
    editCampaignStep,
    closeCampaignStep,
    viewCampaignStep,
    currentEditableStep,
    campaignEditorView: campaignEditorView as CampaignEditorView,
    setCampaignEditorView,
    isAudienceTakeoverOpen,
    isDisplayPriorityTakeoverOpen,
    isDisplayUntilTakeoverOpen,
    isAlertHistorySlideOverOpen,
    setIsAlertHistorySlideOverOpen,
    setIsAudienceTakeoverOpen: handleSetIsAudienceTakeoverOpen,
    setIsDisplayPriorityTakeoverOpen: handleSetIsDisplayPriorityTakeoverOpen,
    setIsDisplayUntilTakeoverOpen: handleSetIsDisplayUntilTakeoverOpen,
  };

  return <EditorContext.Provider value={value}>{children}</EditorContext.Provider>;
};
